import moment, { Moment, MomentInput } from 'moment';

/**
 * Minimal structural shape of a lesson needed to place it on a timeline.
 *
 * Kept intentionally decoupled from the generated GraphQL types: the real
 * roster type (`IGroupLessonRow` from `GroupLessonsQuery`) carries `date`
 * (GraphQL `Date`, ISO `YYYY-MM-DD`) and `startTime` (GraphQL `Time`,
 * `HH:mm[:ss]`) and is therefore assignable to this interface.
 */
export interface ILessonSchedule {
  /** Scheduled calendar date, ISO `YYYY-MM-DD`. */
  date?: string | null;
  /** Optional start time of day, `HH:mm` or `HH:mm:ss`. */
  startTime?: string | null;
}

const DATE_FORMAT = 'YYYY-MM-DD';
const TIME_FORMATS = ['HH:mm:ss', 'HH:mm'];

/**
 * Derive the scheduled instant of a lesson from its calendar date and
 * (optional) start time. Returns `null` when the date is missing or cannot be
 * parsed so the caller can exclude the lesson instead of throwing.
 */
const toScheduledMoment = (lesson: ILessonSchedule): Moment | null => {
  if (!lesson.date) {
    return null;
  }

  const day = moment(lesson.date, DATE_FORMAT, true);
  if (!day.isValid()) {
    return null;
  }

  const rawTime = lesson.startTime?.trim();
  if (rawTime) {
    const time = moment(rawTime, TIME_FORMATS, true);
    if (time.isValid()) {
      day.hour(time.hour()).minute(time.minute()).second(time.second());
    }
  }

  return day;
};

/**
 * Select the lessons scheduled at or after `referenceTime`, ordered
 * non-decreasingly by their scheduled datetime (chronological).
 *
 * Pure and total: lessons with a missing or unparseable date are excluded
 * rather than throwing, and an unparseable `referenceTime` yields an empty
 * result (no lesson can be proven to be "upcoming" relative to it).
 *
 * @param lessons        Candidate lessons (any type carrying date/startTime).
 * @param referenceTime  Cutoff instant; accepts a `Date`, ISO string, epoch
 *                       millis, or any other moment-compatible value.
 * @returns The upcoming lessons in chronological order (input type preserved).
 */
export function selectUpcomingLessons<TLesson extends ILessonSchedule>(
  lessons: readonly TLesson[],
  referenceTime: MomentInput,
): TLesson[] {
  if (!Array.isArray(lessons)) {
    return [];
  }

  const reference = moment(referenceTime);
  if (!reference.isValid()) {
    return [];
  }

  const referenceMillis = reference.valueOf();

  return lessons
    .map((lesson) => ({ lesson, scheduled: toScheduledMoment(lesson) }))
    .filter(
      (entry): entry is { lesson: TLesson; scheduled: Moment } =>
        entry.scheduled !== null &&
        entry.scheduled.valueOf() >= referenceMillis,
    )
    .sort((a, b) => a.scheduled.valueOf() - b.scheduled.valueOf())
    .map((entry) => entry.lesson);
}
